<form [formGroup]="symptomsForm" (ngSubmit)="onSubmit()" class="q-screen" *ngIf="!isSubmitting; else loader">

  <div class="section-intro">
    <div class="si-progress" role="progressbar" [attr.aria-valuenow]="answeredCount"
         aria-valuemin="0" [attr.aria-valuemax]="progressTotal"
         [attr.aria-valuetext]="'symptoms.progress' | translate:{ done: answeredCount, total: progressTotal }">
      <div class="si-track"><div class="si-fill" [style.width.%]="answeredCount / progressTotal * 100"></div></div>
      <div class="si-count">{{ 'symptoms.progress' | translate:{ done: answeredCount, total: progressTotal } }}</div>
    </div>
    <div class="si-title">{{ 'symptoms.title' | translate }}</div>
    <div class="si-desc">{{ 'symptoms.subtitle' | translate }}</div>
  </div>

  <!-- Q1: age -->
  <div class="q-block">
    <div class="q-label">{{ 'symptoms.ageLabel' | translate }} <span class="req">*</span></div>
    <input type="number" class="age-input" formControlName="age" inputmode="numeric"
           [min]="minAge" [max]="maxAge"
           [placeholder]="'symptoms.agePlaceholder' | translate">
    <div class="q-err" *ngIf="showAgeError">{{ ageError }}</div>
    <div class="age-note" *ngIf="showAgeNote">{{ 'symptoms.ageComment' | translate }}</div>
  </div>

  <!-- Q2: menstrual bleeding in last 12 months -->
  <div class="q-block">
    <div class="q-label">{{ 'symptoms.menstrualQuestion' | translate }} <span class="req">*</span></div>
    <div class="q-row">
      <div class="q-opt" [class.sel]="symptomsForm.get('period_last_12')?.value === 'yes'"
           (click)="setYesNo('period_last_12','yes')">{{ 'common.buttons.yes' | translate }}</div>
      <div class="q-opt" [class.sel]="symptomsForm.get('period_last_12')?.value === 'no'"
           (click)="setYesNo('period_last_12','no')">{{ 'common.buttons.no' | translate }}</div>
    </div>
    <div class="q-err" *ngIf="submitted && symptomsForm.get('period_last_12')?.invalid">
      {{ 'symptoms.selectRequired' | translate }}
    </div>
  </div>

  <!-- Q2b: how long ago the last period was. Optional, and buckets rather than
       a date, so it can be answered from memory and compared over time. -->
  <div class="q-block">
    <div class="q-label">{{ 'symptoms.lastPeriod.question' | translate }}</div>
    <div class="q-chips">
      <div class="q-chip" *ngFor="let o of visibleLastPeriodOptions"
           [class.sel]="symptomsForm.get('last_period')?.value === o.key"
           (click)="setLastPeriod(o.key)">{{ o.label | translate }}</div>
    </div>
  </div>

  <!-- Q2c: contraception, asked of EVERYONE (Danna 22.09). Sits outside the
       no-bleeding branch on purpose: a woman who still bleeds can be on a coil
       or a pill too, and the type changes how her cycle reads. -->
  <div class="q-block">
    <div class="q-label">{{ 'symptoms.contraception.question' | translate }}</div>
    <div class="q-sub-note">{{ 'symptoms.contraception.hint' | translate }}</div>
    <div class="q-chips">
      <div class="q-chip" *ngFor="let c of contraceptionOptions"
           [class.sel]="isContraceptionSelected(c.key)"
           (click)="toggleContraception(c.key)">{{ c.label | translate }}</div>
    </div>
  </div>

  <!-- Q2 follow-up: no bleeding reasons -->
  <div class="q-block followup" *ngIf="symptomsForm.get('period_last_12')?.value === 'no'">
    <div class="q-label">{{ 'symptoms.noBleedReasonQuestion' | translate }}</div>
    <div class="q-chips">
      <div class="q-chip" *ngFor="let r of noBleedReasonOptions"
           [class.sel]="isNoBleedReasonSelected(r.key)"
           (click)="toggleNoBleedReason(r.key)">{{ r.label | translate }}</div>
    </div>

    <!-- Surgical procedure: which one, how many ovaries, and the timeline
         between the procedure and any hormone therapy that followed it. -->
    <div class="surgery" *ngIf="showSurgeryFollowup">
      <div class="q-sub">
        <div class="q-label">{{ 'symptoms.surgery.procedureQuestion' | translate }}</div>
        <div class="q-chips">
          <div class="q-chip" *ngFor="let p of surgeryProcedureOptions"
               [class.sel]="symptomsForm.get('surgery_procedure')?.value === p.key"
               (click)="setSurgeryProcedure(p.key)">{{ p.label | translate }}</div>
        </div>
      </div>

      <div class="q-sub" *ngIf="showOvaryCount">
        <div class="q-label">{{ 'symptoms.surgery.ovaryCountQuestion' | translate }}</div>
        <div class="q-row">
          <div class="q-opt" *ngFor="let o of ovaryCountOptions"
               [class.sel]="symptomsForm.get('surgery_ovaries_removed')?.value === o.key"
               (click)="setOvaryCount(o.key)">{{ o.label | translate }}</div>
        </div>
      </div>

      <div class="q-sub">
        <div class="q-label">{{ 'symptoms.surgery.yearQuestion' | translate }}</div>
        <input type="number" class="year-input" formControlName="surgery_year"
               inputmode="numeric" [min]="minYear" [max]="maxYear"
               [placeholder]="'symptoms.surgery.yearPlaceholder' | translate">
        <div class="q-err" *ngIf="yearError('surgery_year')">
          {{ 'symptoms.surgery.yearInvalid' | translate:{ min: minYear, max: maxYear } }}
        </div>
      </div>

      <div class="q-sub">
        <div class="q-label">{{ 'symptoms.surgery.hormoneQuestion' | translate }}</div>
        <div class="q-row">
          <div class="q-opt" *ngFor="let h of hormoneOptions"
               [class.sel]="symptomsForm.get('surgery_hormone_therapy')?.value === h.key"
               (click)="setHormoneTherapy(h.key)">{{ h.label | translate }}</div>
        </div>
      </div>

      <div class="q-sub" *ngIf="showHormoneYear">
        <div class="q-label">{{ 'symptoms.surgery.hormoneYearQuestion' | translate }}</div>
        <input type="number" class="year-input" formControlName="surgery_hormone_year"
               inputmode="numeric" [min]="minYear" [max]="maxYear"
               [placeholder]="'symptoms.surgery.yearPlaceholder' | translate">
        <div class="q-err" *ngIf="yearError('surgery_hormone_year')">
          {{ 'symptoms.surgery.yearInvalid' | translate:{ min: minYear, max: maxYear } }}
        </div>
      </div>
    </div>
  </div>

  <!-- Q3: recent changes (chips) -->
  <div class="q-block">
    <div class="q-label">{{ 'symptoms.checkSymptoms' | translate }} <span class="req">*</span></div>
    <div class="q-chips">
      <div class="q-chip" *ngFor="let c of symptomChips"
           [class.sel]="symptomsForm.get(c.key)?.value" (click)="toggleSymptom(c.key)">{{ c.label | translate }}</div>
      <div class="q-chip" [class.sel]="symptomsForm.get('other_enabled')?.value"
           (click)="toggleOther()">{{ 'symptoms.symptomsList.otherSymptoms' | translate }}</div>
      <div class="q-chip none" [class.sel]="noneSelected"
           (click)="toggleNone()">{{ 'symptoms.symptomsList.noneOfThese' | translate }}</div>
    </div>
    <div class="other-input" *ngIf="symptomsForm.get('other_enabled')?.value">
      <input type="text" formControlName="other_text" [placeholder]="'symptoms.symptomsList.pleaseSpecify' | translate">
    </div>
    <div class="q-err" *ngIf="submitted && !hasRecentChangesAnswer">
      {{ 'symptoms.recentChangesRequired' | translate }}
    </div>
  </div>

  <button type="submit" class="q-submit" [disabled]="!canSubmit">
    {{ (isSubmitting ? 'symptoms.analyzing' : 'symptoms.next') | translate }}
  </button>
</form>
<ng-template #loader>
  <app-loader></app-loader>
</ng-template>
